/**
 * Validation service for the New Posting Expereince (page-media-edit.tsx & block-card.tsx)
 */
import moment, { Moment } from 'moment';
import { PlatformScheduleDTO } from '../dto/platform-schedule.dto';
import { parseHashtags, parseUserTags } from '../utils/blockCardHelper';
import isValidHttpUrl from '../utils/url';
import { PlatformScheduleAssetDTO } from '../components';

/**
 * Method to compare the min max value (inclusive)
 * @param value
 * @param min
 * @param max
 * @returns boolean
 */
export function validateMinMaxInclusive(value: number, min: number, max: number) {
  if (value >= min && value <= max) {
    return true;
  }
  return false;
}

/**
 * Method to compare the min max value
 * @param value
 * @param min
 * @param max
 * @returns boolean
 */
export function validateMinMax(value: number, min: number, max: number) {
  if (value > min && value < max) {
    return true;
  }
  return false;
}

/**
 * Method to compare the greater than value
 * @param value
 * @param gtValue
 * @returns boolean
 */
export function validateGreaterThan(value: number | Date | Moment, gtValue: number | string | Moment) {
  if (gtValue === 'now') {
    gtValue = moment();
  }
  if (value > gtValue) {
    return true;
  }
  return false;
}

/**
 * Method to compare the less than value
 * @param value
 * @param ltValue
 * @returns boolean
 */
export function validateLessThan(value: number | Date, ltValue: number | string | Moment) {
  if (ltValue === 'now') {
    ltValue = moment();
  }
  if (value < ltValue) {
    return true;
  }
  return false;
}

/**
 * Method to validate the max dimensions
 * @param width
 * @param height
 * @param ruleWidth
 * @param ruleHeight
 * @returns
 */
export function validateMaxDimensions(width: string | number, height: string | number, ruleWidth: string | number, ruleHeight: string | number) {
  if (width < ruleWidth && height < ruleHeight) {
    return true;
  }

  return false;
}

/**
 * Method to validate the max dimensions
 * @param width
 * @param height
 * @param ruleWidth
 * @param ruleHeight
 * @returns
 */
export function validateMaxWidthInclusive(width: number, ruleWidth: number) {
  if (width <= ruleWidth) {
    return true;
  }

  return false;
}

/**
 * Method to validate the max dimensions
 * @param width
 * @param height
 * @param ruleWidth
 * @param ruleHeight
 * @returns
 */
export function validateMaxDimensionsInclusive(width: string | number, height: string | number, ruleWidth: string | number, ruleHeight: string | number) {
  if (width <= ruleWidth && height <= ruleHeight) {
    return true;
  }

  return false;
}

/**
 * Method to validate the minimum dimensions
 * @param width
 * @param height
 * @param ruleWidth
 * @param ruleHeight
 * @returns
 */
export function validateMinDimensions(width: number, height: number, ruleWidth: number, ruleHeight: number) {
  if (width > ruleWidth && height > ruleHeight) {
    return true;
  }

  return false;
}

/**
 * Method to validate file size
 * @param fileSize
 * @param limit
 * @returns
 */
export function validateFileSize(fileSize: number, limit: number) {
  return fileSize <= limit;
}

/**
 * Method to validate file size
 * @param fileSize
 * @param postType
 * @returns
 */
export function validateMediaFileSize(fileSize: number, fileType: string[], postType: any) {
  const rules = postType.validations;
  let rule;
  if (fileType.includes('video')) {
    rule = rules.find((item: { type: string }) => item.type === 'autoPostVideoSize');
  } else if (fileType.includes('jpeg') || fileType.includes('png') || fileType.includes('bmp') || fileType.includes('webp')) {
    rule = rules.find((item: { type: string }) => item.type === 'autoPostImageSize');
  } else if (fileType.includes('gif')) {
    rule = rules.find((item: { type: string }) => item.type === 'autoPostGIFSize');
  }
  // If there is no rule return null
  if (!rule) {
    return [];
  }
  if (fileSize / 1000000 > rule.parameters.value) {
    return [
      {
        id: rule.type,
        message: rule.userErrorMessage,
        link: rule.errorHelpLink
      }
    ];
  }
  return [];
}

/**
 * Method to validate the allowed media types
 * @param uploadedTypes
 * @param allowedTypes array of mime types e.g. text/plain
 * @returns
 */
export function validateAllowedTypes(uploadedTypes: any[], allowedTypes: string | any[]) {
  return uploadedTypes.every((item) => allowedTypes.includes(item));
}

/**
 * Method to validate the block card based on the platform configuration file
 * @param type null for validating everything, else it will validate the specific type
 * @param value the value of what is to be validated
 * @param postType the type of post for the platform. e.g. reels or story, etc...that holds the config as well as validations
 */
export function validateAll(
  platformSchedule: PlatformScheduleDTO,
  postType: any,
  assetElms: Array<HTMLVideoElement | HTMLImageElement> = [],
  numAssets: number,
  isSchedule = false,
  firstCommentEnabled = false
) {
  let errors: (string | { id: string; message: any; link: any })[] = [];

  // We do not validate drafts ONLY when they are scheduled
  if (isSchedule === false) {
    return errors;
  }

  // Go through all the rules for the platform
  // NOTE: for every new validation that is in the config we will need to create it's corresponding function
  postType.validations.forEach((rule: { type: string }) => {
    if (rule.type === 'futureDate') {
      errors = [...errors, ...validateDate(platformSchedule, postType)];
    } else if (rule.type === 'numberOfAssets') {
      // Only validate number of assets if scheduling. Drafts can have zero assets
      errors = [...errors, ...validateNumberOfAssets(platformSchedule, postType)];
    } else if (rule.type === 'autoPostCaptionLength' && platformSchedule.isEnabled) {
      errors = [...errors, ...validateCaption(platformSchedule, postType)];
    } else if (rule.type === 'manualPostCaptionLength' && !platformSchedule.isEnabled) {
      errors = [...errors, ...validateCaption(platformSchedule, postType)];
    } else if (rule.type === 'autoPostVideoLength' && platformSchedule.isEnabled) {
      errors = [...errors, ...validateVideoLength(postType, assetElms)];
    } else if (rule.type === 'autoPostVideoDimensions' && platformSchedule.isEnabled) {
      // MIN
      errors = [...errors, ...validateVideoMinDimensions(postType, assetElms)];
    } else if (rule.type === 'autoPostVideoMaxDimensions' && platformSchedule.isEnabled) {
      // MAX
      errors = [...errors, ...validateVideoMaxDimensions(postType, assetElms)];
    } else if (rule.type === 'allowedMediaTypes' && platformSchedule.isEnabled) {
      errors = [...errors, ...validateAllowedMediaTypes(platformSchedule, postType, numAssets)];
    } else if (rule.type === 'aspectRatio' && platformSchedule.isEnabled) {
      errors = [...errors, ...validateAspectRatio(assetElms, postType)];
    } else if (rule.type === 'videoAspectRatio' && platformSchedule.isEnabled) {
      errors = [...errors, ...validateAspectRatio(assetElms, postType)];
    } else if (rule.type === 'hashtagLimit' && postType.allowHashtags) {
      errors = [...errors, ...validateHashtagLength(platformSchedule, postType, firstCommentEnabled)];
    } else if (rule.type === 'firstCommentMentionLimit' && platformSchedule.isEnabled && firstCommentEnabled) {
      errors = [...errors, ...validateFirstCommentMentionLimit(platformSchedule, postType, firstCommentEnabled)];
    } else if (rule.type === 'aspectRatioRange' && platformSchedule.isEnabled) {
      errors = [...errors, ...validateAspectRatioRange(assetElms, postType)];
    } else if (rule.type === 'nonEmptyPost' && platformSchedule.isEnabled) {
      errors = [...errors, ...validatePostIsNotEmpty(assetElms, platformSchedule, postType)];
    } else if (rule.type === 'firstCommentLength' && platformSchedule.isEnabled && firstCommentEnabled) {
      errors = [...errors, ...validateFirstComment(platformSchedule, postType)];
    }
  });
  return errors;
}

/**
 * Method to validate that uploaded media types are allowed for the current post type
 * @param platformSchedule
 * @param postType
 * @returns
 */
export function validateAllowedMediaTypes(platformSchedule: PlatformScheduleDTO, postType: any, numAssets: number) {
  const rule = postType.validations.find((item: { type: string }) => item.type === 'allowedMediaTypes');
  if (!rule) return [];
  const allowableMediaTypes = postType.allowMultipleAssets
    ? numAssets > 1
      ? postType.allowedMultipleAssetMediaTypes
      : postType.allowedMediaTypes
    : postType.allowedMediaTypes;
  const uploadedMediaTypes = platformSchedule?.platformScheduleAssets?.map((asset) => asset?.contentType) ?? [];
  const hasValidMediaTypes = validateAllowedTypes(uploadedMediaTypes, allowableMediaTypes);
  if (!hasValidMediaTypes) {
    return [
      {
        id: 'allowedMediaTypes',
        message: rule.userErrorMessage,
        link: rule.errorHelpLink
      }
    ];
  }
  return [];
}

/**
 * Method to validate the date to make sure it's a future date
 * @param platformSchedule
 * @param postType
 * @returns
 */
export function validateDate(platformSchedule: PlatformScheduleDTO, postType: any) {
  const rules = postType.validations;
  // Get either the AP rule or Manual Posting rule based on apEnabled
  const rule = rules.find((item: { type: string }) => item.type === 'futureDate');

  if (!rule) return [];

  if (!platformSchedule.scheduleDate || validateGreaterThan(platformSchedule.scheduleDate, 'now') === false) {
    return [
      {
        id: 'futureDate',
        message: rule.userErrorMessage,
        link: rule.errorHelpLink
      }
    ];
  }

  return [];
}

/**
 * Method to validate video size
 * @param postType
 * @param assetElms
 * @returns
 */
export function validateVideoMaxDimensions(postType: any, assetElms: Array<HTMLVideoElement | HTMLImageElement> = []) {
  const errors: any[] = [];
  const errorItems: any[] = [];
  // IF there are no assets to verify just return
  if (assetElms.length === 0) return errors;
  // Get the videos
  const videoElms = assetElms.filter((item) => item.nodeName !== 'IMG');
  if (videoElms.length === 0) return errors;
  // Get the rule
  const rule = postType.validations.find((item: { type: string }) => item.type === 'autoPostVideoMaxDimensions');
  if (!rule) return errors;

  assetElms.forEach((video: HTMLVideoElement, index) => {
    // LOOP through ALL of the asset to keep the index correct BUT only test the videos
    if (video.nodeName !== 'IMG') {
      // make sure the width and heigh don't go under the min dimensions
      if (validateMaxWidthInclusive(video.videoWidth, rule.parameters.width) === false) {
        errorItems.push(index + 1);
      }
    }
  });

  if (errorItems.length) {
    errors.push({
      id: 'autoPostVideoMaxDimensions',
      message: rule.userErrorMessage + ` Please fix media ${errorItems.join(', ')}.`,
      link: rule.errorHelpLink
    });
  }
  return errors;
}

/**
 * Method to validate video size
 * @param postType
 * @param assetElms
 * @returns
 */
export function validateVideoMinDimensions(postType: any, assetElms: Array<HTMLVideoElement | HTMLImageElement> = []) {
  const errors: any[] = [];
  const errorItems: any[] = [];
  // IF there are no assets to verify just return
  if (assetElms.length === 0) return errors;
  // Get the videos
  const videoElms = assetElms.filter((item) => item.nodeName !== 'IMG');
  if (videoElms.length === 0) return errors;
  // Get the rule
  const rule = postType.validations.find((item: { type: string }) => item.type === 'autoPostVideoDimensions');
  if (!rule) return errors;
  assetElms.forEach((video: HTMLVideoElement, index) => {
    // LOOP through ALL of the asset to keep the index correct BUT only test the videos
    if (video.nodeName !== 'IMG') {
      // make sure the width and heigh don't go under the min dimensions
      if (validateMinDimensions(video.videoWidth, video.videoHeight, rule.parameters.width, rule.parameters.height) === false) {
        errorItems.push(index + 1);
      }
    }
  });

  if (errorItems.length) {
    errors.push({
      id: 'autoPostVideoDimensions',
      message: rule.userErrorMessage + ` Please fix media ${errorItems.join(', ')}.`,
      link: rule.errorHelpLink
    });
  }
  return errors;
}

/**
 * Method to validate ALL videos
 * @param postType
 * @param assetElms
 * @returns array of errors
 */
export function validateVideoLength(postType: any, assetElms: Array<HTMLVideoElement | HTMLImageElement> = []) {
  const errors: any[] = [];
  const errorItems = [];
  // IF there are no assets to verify just return
  if (assetElms.length === 0) return errors;
  // Get the videos
  const videoElms = assetElms.filter((item) => item.nodeName !== 'IMG');
  if (videoElms.length === 0) return errors;
  // Get the rule
  const rule = postType.validations.find((item: { type: string }) => item.type === 'autoPostVideoLength');
  if (!rule) return errors;
  assetElms.forEach((video: HTMLVideoElement, index) => {
    // LOOP through ALL of the asset to keep the index correct BUT only test the videos
    if (video.nodeName !== 'IMG') {
      if (validateMinMaxInclusive(video.duration, rule.parameters.min, rule.parameters.max) === false) {
        errorItems.push(index + 1);
      }
    }
  });
  if (errorItems.length) {
    errors.push({
      id: 'autoPostVideoLength',
      message: rule.userErrorMessage,
      link: rule.errorHelpLink
    });
  }
  return errors;
}

/**
 * Method to validate the number of assets
 * @param platformSchedule
 * @param postType
 * @returns
 */
export function validateNumberOfAssets(platformSchedule: PlatformScheduleDTO, postType: any) {
  const rules = postType.validations;
  // Get either the AP rule or Manual Posting rule based on apEnabled
  const rule = rules.find((item: { type: string }) => item.type === 'numberOfAssets');

  if (!rule) return [];

  if (validateMinMaxInclusive(platformSchedule.platformScheduleAssets.length, rule.parameters.min, rule.parameters.max) === false) {
    const error = [
      {
        id: 'numberOfAssets', // this is a general name since this could be manual or autopost
        message: rule.userErrorMessage,
        link: rule.errorHelpLink
      }
    ];
    return error;
  }

  return [];
}

/**
 * Method to validate the number of products
 * @param platformSchedule
 * @param postType
 * @returns
 */
export function validateNumberOfProducts(platformSchedule: PlatformScheduleDTO, postType: any) {
  const rules = postType.validations;
  const rule = rules.find((item: { type: string }) => item.type === 'numberOfProducts');

  if (!rule) return [];

  const productMetadataElements = (platformSchedule?.platformScheduleMetadata ?? []).filter((item) => item.name === 'amazonProducts');

  const products = productMetadataElements.flatMap((metadataElement) => {
    const parsedArray = JSON.parse(metadataElement.value);
    if (!Array.isArray(parsedArray)) {
      throw new Error(`amazonProducts metadata value has an unexpected shape. Expected a JSON array but got ${metadataElement.value}`);
    }
    return parsedArray;
  });

  if (!validateMinMaxInclusive(products.length, rule.parameters.min, rule.parameters.max)) {
    const error = [
      {
        id: 'numberOfProducts',
        message: rule.userErrorMessage,
        link: rule.errorHelpLink
      }
    ];
    return error;
  }

  return [];
}

/**
 * Method to validate the caption
 * @param platformSchedule
 * @param postType
 * @returns
 */
export function validateCaption(platformSchedule: PlatformScheduleDTO, postType: any) {
  const apEnabled = platformSchedule.isEnabled;
  const caption = platformSchedule.caption;
  const rules = postType.validations;
  // Get either the AP rule or Manual Posting rule based on apEnabled
  const rule =
    apEnabled === true
      ? rules.find((item: { type: string }) => item.type === 'autoPostCaptionLength')
      : rules.find((item: { type: string }) => item.type === 'manualPostCaptionLength');
  // If there is no rule for some reason just return null
  if (!rule) return [];
  // Validate
  if (validateMinMaxInclusive(caption.length, rule.parameters.min, rule.parameters.max) === false) {
    const error = [
      {
        id: 'caption', // this is a general name since this could be manual or autopost
        message: rule.userErrorMessage,
        link: rule.errorHelpLink
      }
    ];
    return error;
  }

  return [];
}

/**
 * Method to validate the first comment
 * @param platformSchedule
 * @param postType
 * @returns
 */
export function validateFirstComment(platformSchedule: PlatformScheduleDTO, postType: any) {
  const { platformScheduleComment } = platformSchedule;
  const firstComment = platformScheduleComment?.comment;
  const { allowFirstComment, validations: rules } = postType;
  // Get either the AP rule or Manual Posting rule based on apEnabled
  const rule = rules.find((item: { type: string }) => item.type === 'firstCommentLength');
  // If there is no rule for some reason just return null
  if (!rule || !allowFirstComment || !firstComment) return [];
  // Validate
  if (validateMinMaxInclusive(firstComment.length, rule.parameters.min, rule.parameters.max) === false) {
    const error = [
      {
        id: 'firstComment', // this is a general name since this could be manual or autopost
        message: rule.userErrorMessage,
        link: rule.errorHelpLink
      }
    ];
    return error;
  }

  return [];
}

/**
 * Method to validate the caption
 * @param platformSchedule
 * @param postType
 * @returns
 */
export function validateTitle(title: string, postType: any) {
  const rules = postType.validations;
  // Get either the AP rule or Manual Posting rule
  const rule = rules.find((item: { type: string }) => item.type === 'titleLength');
  // If there is no rule for some reason just return null
  if (!rule) return [];
  const titleLength = title?.length ?? 0;
  // Validate
  if (validateMinMaxInclusive(titleLength, rule.parameters.min, rule.parameters.max) === false) {
    const error = [
      {
        id: 'titleLength',
        message: rule.userErrorMessage,
        link: rule.errorHelpLink
      }
    ];
    return error;
  }

  return [];
}

/**
 * Method to validate the hashtagLength
 * @param platformSchedule
 * @param postType
 * @returns
 */
export function validateHashtagLength(platformSchedule: PlatformScheduleDTO, postType: any, firstCommentEnabled = false) {
  const { caption, platformScheduleComment } = platformSchedule;
  const { allowFirstComment, validations: rules } = postType;
  const hashtagCount = firstCommentEnabled && allowFirstComment ? parseHashtags(caption) + parseHashtags(platformScheduleComment?.comment) : parseHashtags(caption);
  const hashtagLengthRule = rules.find((item: { type: string }) => item.type === 'hashtagLimit');

  if (postType.allowHashtags && hashtagLengthRule && validateMinMaxInclusive(hashtagCount, hashtagLengthRule.parameters.min, hashtagLengthRule.parameters.max) === false) {
    const error = [
      {
        id: 'hashtagLength', // this is a general name since this could be manual or autopost
        message: hashtagLengthRule.userErrorMessage,
        link: ''
      }
    ];
    return error;
  }

  return [];
}

/**
 * Method to validate the first comment mention limit
 * @param platformSchedule
 * @param postType
 * @returns
 */
export function validateFirstCommentMentionLimit(platformSchedule: PlatformScheduleDTO, postType: any, firstCommentEnabled = false) {
  const { platformScheduleComment } = platformSchedule;
  const { allowFirstComment, validations: rules } = postType;
  const mentionCount = firstCommentEnabled && allowFirstComment ? parseUserTags(platformScheduleComment?.comment) : 0;
  const firstCommentMentionLimitRule = rules.find((item: { type: string }) => item.type === 'firstCommentMentionLimit');

  if (
    allowFirstComment &&
    firstCommentMentionLimitRule &&
    validateMinMaxInclusive(mentionCount, firstCommentMentionLimitRule.parameters.min, firstCommentMentionLimitRule.parameters.max) === false
  ) {
    const error = [
      {
        id: 'firstCommentMentionLimit', // this is a general name since this could be manual or autopost
        message: firstCommentMentionLimitRule.userErrorMessage,
        link: ''
      }
    ];
    return error;
  }

  return [];
}

/* the binary Great Common Divisor calculator */
function gcd(u: number, v: number): number {
  if (u === v) return u;
  if (u === 0) return v;
  if (v === 0) return u;

  if (~u & 1)
    if (v & 1) return gcd(u >> 1, v);
    else return gcd(u >> 1, v >> 1) << 1;

  if (~v & 1) return gcd(u, v >> 1);

  if (u > v) return gcd((u - v) >> 1, v);

  return gcd((v - u) >> 1, u);
}

/**
 * Method to find the aspect ratio and determine if it's acceptiable based on the config
 * @param width
 * @param height
 * @param postType
 * @returns
 */
export function validateAspectRatio(assetElms: Array<HTMLVideoElement | HTMLImageElement> = [], postType: any) {
  const errorItems = [];
  const errorSources = [];

  if (assetElms.length === 0) {
    return [];
  }
  const rules = postType.validations;
  const rule = rules.find((item: { type: string }) => item.type === 'aspectRatio');
  // If there is no rule for some reason just return null
  if (!rule) return [];
  const acceptedRatios = rule.parameters;

  // This violation pre-dates the introduction of @typescript-eslint/no-unused-vars
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let t = '';
  assetElms.forEach((asset, index) => {
    //indicates if the full URL of the element is currently visible
    if (!asset.currentSrc) {
      return;
    }
    // LOOP through ALL of the asset to keep the index correct BUT only test the images
    if (asset.nodeName === 'IMG') {
      const d = gcd(asset.width, asset.height);
      const result = `${asset.width / d}:${asset.height / d}`;

      if (!acceptedRatios.includes(result)) {
        errorItems.push(index + 1);
        if (asset?.src) errorSources.push(asset.src);
      }
    } else if (asset.nodeName === 'VIDEO') {
      // @ts-ignore
      const d = gcd(asset.videoWidth, asset.videoHeight);
      // @ts-ignore
      const result = `${asset.videoWidth / d}:${asset.videoHeight / d}`;
      // @ts-ignore
      t = `${asset.videoWidth} ${asset.videoHeight}`;

      if (!acceptedRatios.includes(result)) {
        errorItems.push(index + 1);
        if (asset?.src) errorSources.push(asset.src);
      }
    }
  });

  if (errorItems.length) {
    return [
      {
        id: 'aspectRatio',
        message: rule.userErrorMessage,
        link: rule.errorHelpLink,
        errorSources
      }
    ];
  }

  return [];
}

/**
 * Method to find the aspect ratio of specifically videos and determine if it's acceptiable based on the config
 * NOTE: this is to help validations where both images and videos are accepected BUT we only want to run aspect validations
 * for videos only
 * @param width
 * @param height
 * @param postType
 * @returns
 */
export function validateVideoAspectRatio(assetElms: Array<HTMLVideoElement> = [], postType: any) {
  const errorItems = [];
  const errorSources = [];

  if (assetElms.length === 0) {
    return [];
  }
  const rules = postType.validations;
  const rule = rules.find((item: { type: string }) => item.type === 'videoAspectRatio');
  // If there is no rule for some reason just return null
  if (!rule) return [];
  const acceptedRatios = rule.parameters;

  // This violation pre-dates the introduction of @typescript-eslint/no-unused-vars
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let t = '';
  assetElms.forEach((asset, index) => {
    //indicates if the full URL of the element is currently visible
    if (!asset.currentSrc) {
      return;
    }
    // LOOP through ALL of the asset to keep the index correct BUT only test the videos
    if (asset.nodeName === 'VIDEO') {
      // @ts-ignore
      const d = gcd(asset.videoWidth, asset.videoHeight);
      // @ts-ignore
      const result = `${asset.videoWidth / d}:${asset.videoHeight / d}`;
      // @ts-ignore
      t = `${asset.videoWidth} ${asset.videoHeight}`;

      if (!acceptedRatios.includes(result)) {
        errorItems.push(index + 1);
        if (asset?.src) errorSources.push(asset.src);
      }
    }
  });

  if (errorItems.length) {
    return [
      {
        id: 'videoAspectRatio',
        message: rule.userErrorMessage,
        link: rule.errorHelpLink,
        errorSources
      }
    ];
  }

  return [];
}

/**
 * Method to find the aspect ratio and determine if it's acceptiable based on the config
 * @param assetElms
 * @param postType
 * @returns
 */
export function validateAspectRatioRange(assetElms: Array<HTMLVideoElement | HTMLImageElement> = [], postType: any) {
  const errorItems = [];
  const errorSources = [];

  if (assetElms.length === 0) {
    return [];
  }
  const rules = postType.validations;
  const rule = rules.find((item: { type: string }) => item.type === 'aspectRatioRange');
  // If there is no rule for some reason just return null
  if (!rule) return [];
  // const acceptedRatios = rule.parameters;
  if (!rule.parameters.imgMin && !rule.parameters.videoMin) return [];

  const imgMinRatio = rule.parameters.imgMin ? rule.parameters.imgMin.split(':')[0] / rule.parameters.imgMin.split(':')[1] : null;
  const imgMaxRatio = rule.parameters.imgMax ? rule.parameters.imgMax.split(':')[0] / rule.parameters.imgMax.split(':')[1] : null;

  const videoMinRatio = rule.parameters.videoMin ? rule.parameters.videoMin.split(':')[0] / rule.parameters.videoMin.split(':')[1] : null;
  const videoMaxRatio = rule.parameters.videoMax ? rule.parameters.videoMax.split(':')[0] / rule.parameters.videoMax.split(':')[1] : null;

  assetElms.forEach((asset, index) => {
    // LOOP through ALL of the asset to keep the index correct BUT only test the images
    if (asset.nodeName === 'IMG') {
      if (imgMinRatio) {
        const d = gcd(asset.width, asset.height);
        const imgRatio = asset.width / d / (asset.height / d);

        if (imgRatio < imgMinRatio || imgRatio > imgMaxRatio) {
          errorItems.push(index + 1);
          if (asset?.src) errorSources.push(asset.src);
        }
      }
    } else if (asset.nodeName === 'VIDEO') {
      if (videoMinRatio) {
        // @ts-ignore
        const d = gcd(asset.videoWidth, asset.videoHeight);
        // @ts-ignore
        const videoRatio = asset.videoWidth / d / (asset.videoHeight / d);

        if (isNaN(videoRatio) || videoRatio < videoMinRatio || videoRatio > videoMaxRatio) {
          errorItems.push(index + 1);
          if (asset?.src) errorSources.push(asset.src);
        }
      }
    }
  });

  if (errorItems.length) {
    return [
      {
        id: 'aspectRatio',
        message: rule.userErrorMessage,
        link: rule.errorHelpLink,
        errorSources
      }
    ];
  }

  return [];
}

/**
 * Method to ensure either a caption or media is present (used by LinkedIn and Twitter which support posts without media)
 * @param assetElms
 * @param platformSchedule
 * @returns
 */
export function validatePostIsNotEmpty(assetElms: Array<HTMLVideoElement | HTMLImageElement> = [], platformSchedule: PlatformScheduleDTO, postType: any) {
  const rules = postType.validations;
  const rule = rules.find((item: { type: string }) => item.type === 'nonEmptyPost');
  // If there is no rule for some reason just return null
  if (!rule) return [];

  const noCaption = !platformSchedule.caption || platformSchedule.caption === '';
  const noMedia = assetElms.length <= 0;
  if (noCaption && noMedia) {
    return [
      {
        id: 'nonEmptyPost',
        message: rule.userErrorMessage,
        link: rule.errorHelpLink
      }
    ];
  }

  return [];
}

/**
 * Platform Specific validations
 * NOTE: these are one off that aren't shared across the other platforms. E.G. Pinterest Boards are specific to Pinterest and has its own validations
 * @param platformSchedule
 * @param platfromConfig
 * @param isSchedule
 * @returns Object[]
 */
export function validatePlatformSpecifics(platformSchedule: PlatformScheduleDTO, platfromConfig: any, isSchedule: boolean) {
  const errors = [];
  /* PINTEREST */
  if (platfromConfig.name === 'pinterest') {
    // We require a board to be selected before a user can schedule a post
    const boardSelected = platformSchedule?.platformScheduleMetadata.find((metadata: { name: string }) => metadata.name === 'board');
    if (isSchedule && !boardSelected?.value) {
      errors.push({
        id: 'pinterestBoardID',
        message: 'Please select a Board to post to in order to meet Pinterest guidelines.',
        link: ''
      });
    }

    // We require a valid url, if the user adds a url, before a user can schedule a post
    const url = platformSchedule?.platformScheduleMetadata.find((metadata: { name: string }) => metadata.name === 'url');
    if (url) {
      // make sure that is there is a url and that its a valid url
      const result = isValidHttpUrl(url.value);
      if (isSchedule && !result) {
        errors.push({
          id: 'pinterestUrl',
          message: 'Link URL is Invalid. Please update or remove in order to schedule for auto-post.',
          link: ''
        });
      }
    }
  }
  /* END PINTEREST */

  /* SNIPFEED URL FOR IG AND TIKTOK */
  if (platfromConfig.name === 'tiktok' || platfromConfig.name === 'instagram') {
    // We require a board to be selected before a user can schedule a post
    const snipfeedUrl = platformSchedule?.platformScheduleMetadata.find((metadata: { name: string }) => metadata.name === 'snipfeedUrl');
    if (snipfeedUrl) {
      const snipfeed = JSON.parse(snipfeedUrl.value);
      if (!snipfeed.title || snipfeed.title.length < 3) {
        errors.push({
          id: 'snipfeedTitle',
          message: 'Unable to schedule: Update the Snipfeed title to contain at least 3 characters',
          link: ''
        });
      }

      // Run the validation IF there is a url present
      if (snipfeed.url) {
        let isValidUrl = false;
        try {
          new URL(snipfeed.url);
          isValidUrl = true;
        } catch (err) {
          isValidUrl = false;
        }
        if (!isValidUrl) {
          errors.push({
            id: 'snipfeedUrl',
            message: 'Please enter a valid Snipfeed url',
            link: ''
          });
        }
      }
    }
  }
  /* END SNIPFEED URL FOR IG AND TIKTOK */

  /* FACEBOOK prevent carousels WITH videos */
  // NOTE: this is a current issue at this moment with the Facebook API
  // (#10) Application does not have permissions for this action
  if (platfromConfig.name === 'facebook') {
    const assets = platformSchedule?.platformScheduleAssets;
    if (assets && assets.length > 1) {
      let foundVideo;
      assets.forEach((asset: PlatformScheduleAssetDTO) => {
        if (asset.contentType.indexOf('video') !== -1) {
          foundVideo = true;
        }
      });
      if (foundVideo === true) {
        errors.push({
          id: 'facebookCarouselWithVideos',
          message: 'Facebook does not allow posting a carousel that contains videos at this time',
          link: ''
        });
      }
    }
  }
  /* END FACEBOOK */
  return errors;
}

export function checkReelInfo(assetElms: Array<HTMLVideoElement | HTMLImageElement> = [], postType: any, infoMessages: any[]) {
  if (assetElms.length === 0) {
    return infoMessages;
  }
  const rules = postType.information;
  const rule = rules?.find((item: { type: string }) => item.type === 'postReelInfo');
  // If there is no rule for some reason just return null
  if (!rule) return infoMessages;

  if (assetElms.length === 1 && assetElms[0].nodeName === 'VIDEO') {
    return [
      ...infoMessages,
      {
        id: 'postReelInfo',
        message: rule.userInformationMessage,
        link: rule.helpLink ? { url: rule.helpLink } : null
      }
    ];
  }

  return infoMessages;
}
